import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState, StatusPill, Loading } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Users, Plus, Loader2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { formatINR } from "@/utils/format";

const EMPTY = {
  full_name: "", employee_id: "", email: "", pan: "", dob: "", doj: "", dol: "",
  designation_id: "", department: "Engineering", monthly_amount: "",
  bank_name: "", bank_account: "", ifsc: "", uan: "", esi_number: "",
};

export default function Employees() {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [designations, setDesignations] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/employees").then(({ data }) => setList(data));
  useEffect(() => {
    load();
    api.get("/designations?include_inactive=false").then(({ data }) => setDesignations(data));
  }, []);

  const create = async () => {
    setSaving(true);
    try {
      const payload = { ...form, monthly_amount: Number(form.monthly_amount) };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      const { data } = await api.post("/employees", payload);
      toast.success(`${data.full_name} added`);
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (list === null) return <Loading />;

  return (
    <div>
      <PageHeader title="Employees" description="New employees get their PAN as the initial password and are forced to change it on first login." testid="employees-page">
        <Button onClick={() => { setForm(EMPTY); setOpen(true); }} className="gap-1.5" data-testid="add-employee-button"><Plus className="w-4 h-4" /> Add employee</Button>
      </PageHeader>

      {list.length === 0 ? (
        <EmptyState icon={Users} title="No employees yet" description="Add your first employee to start running payroll." testid="employees-empty">
          <Button onClick={() => setOpen(true)} className="gap-1.5"><Plus className="w-4 h-4" /> Add employee</Button>
        </EmptyState>
      ) : (
        <div className="bg-card border border-border rounded-md overflow-hidden" data-testid="employees-list">
          <div className="hidden md:grid grid-cols-12 px-5 py-2.5 label-caps border-b border-border bg-secondary/50">
            <div className="col-span-3">Name</div><div className="col-span-2">Emp ID</div>
            <div className="col-span-3">Designation</div><div className="col-span-2 text-right">Monthly</div>
            <div className="col-span-2 text-right">Status</div>
          </div>
          <div className="divide-y divide-border">
            {list.map((e) => (
              <button key={e.id} onClick={() => navigate(`/admin/employees/${e.id}`)}
                className="w-full text-left grid grid-cols-1 md:grid-cols-12 gap-1 md:gap-0 px-5 py-3.5 hover:bg-secondary/40 transition-colors items-center"
                data-testid={`employee-row-${e.employee_id}`}>
                <div className="md:col-span-3 font-medium flex items-center gap-2">{e.full_name}<ChevronRight className="w-4 h-4 text-muted-foreground md:hidden ml-auto" /></div>
                <div className="md:col-span-2 text-sm text-muted-foreground tabular">{e.employee_id}</div>
                <div className="md:col-span-3 text-sm text-muted-foreground">{e.designation || "—"}</div>
                <div className="md:col-span-2 md:text-right text-sm tabular font-medium">{formatINR(e.monthly_amount)}</div>
                <div className="md:col-span-2 md:text-right"><StatusPill active={e.status === "active"} /></div>
              </button>
            ))}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="employee-dialog">
          <DialogHeader><DialogTitle>Add employee</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-4 py-2">
            <F label="Full name *"><Input value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v.target.value })} data-testid="emp-full-name" /></F>
            <F label="Employee ID (auto if blank)"><Input value={form.employee_id} onChange={(v) => setForm({ ...form, employee_id: v.target.value })} placeholder="PC-005" data-testid="emp-id" /></F>
            <F label="Email *"><Input type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v.target.value })} data-testid="emp-email" /></F>
            <F label="PAN *"><Input value={form.pan} onChange={(v) => setForm({ ...form, pan: v.target.value.toUpperCase() })} placeholder="ABCDE1234F" data-testid="emp-pan" /></F>
            <F label="Date of birth"><Input type="date" value={form.dob} onChange={(v) => setForm({ ...form, dob: v.target.value })} /></F>
            <F label="Date of joining *"><Input type="date" value={form.doj} onChange={(v) => setForm({ ...form, doj: v.target.value })} data-testid="emp-doj" /></F>
            <F label="Designation">
              <Select value={form.designation_id} onValueChange={(v) => setForm({ ...form, designation_id: v })}>
                <SelectTrigger data-testid="emp-designation"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{designations.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Department"><Input value={form.department} onChange={(v) => setForm({ ...form, department: v.target.value })} /></F>
            <F label="Monthly amount (₹) *"><Input type="number" value={form.monthly_amount} onChange={(v) => setForm({ ...form, monthly_amount: v.target.value })} data-testid="emp-amount" /></F>
            <F label="Bank name"><Input value={form.bank_name} onChange={(v) => setForm({ ...form, bank_name: v.target.value })} /></F>
            <F label="Bank account number"><Input value={form.bank_account} onChange={(v) => setForm({ ...form, bank_account: v.target.value })} data-testid="emp-account" /></F>
            <F label="IFSC"><Input value={form.ifsc} onChange={(v) => setForm({ ...form, ifsc: v.target.value.toUpperCase() })} /></F>
            <F label="UAN (optional)"><Input value={form.uan} onChange={(v) => setForm({ ...form, uan: v.target.value })} /></F>
            <F label="ESI number (optional)"><Input value={form.esi_number} onChange={(v) => setForm({ ...form, esi_number: v.target.value })} /></F>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={saving || !form.full_name || !form.email || !form.pan || !form.doj || !form.monthly_amount} data-testid="save-employee-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create employee"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children }) {
  return <div><Label className="label-caps">{label}</Label><div className="mt-1.5">{children}</div></div>;
}
